import React, { useState, useRef, useEffect, useMemo } from 'react';
import { TextField, Typography, IconButton, InputAdornment, Box, Checkbox, CircularProgress, FormControl, Select, MenuItem, Button, ListItemText } from '@mui/material';
import { AddButton } from '../button/button';
import DeleteIcon from '@mui/icons-material/Delete';
import { ChromePicker } from 'react-color';
import ColorizeIcon from '@mui/icons-material/Colorize';
import { useNavigate, useParams } from 'react-router-dom';
import { createConfig, getConfigCategoryList, getConfigForTable, setCreateConfigStatus, setUpdateConfigStatus, updateConfig } from '../../redux/slices/config';
import { AppDispatch, RootState } from '../../redux/store/appStore';
import { useDispatch, useSelector } from 'react-redux';
import SnackbarNotification from '../snackbar/snackbarNotification';
import { STATUS } from '../../utils/status';
import debounce from 'lodash.debounce';
import { getReview } from '../../redux/slices/reviewerSlice';
import { ConfigIds } from './config';
import RichTextEditor from '../project/textEditor';
import AddIcon from '@mui/icons-material/Add'
import { setSelectedStageId } from '../../redux/slices/reviewerSlice/index';
import CustomRichTextEditor from '../CustomRichTextEditor/CustomRichTextEditor';
import ScoringConfigSection from './ScoringConfigSection';

interface JsonTypeContentProps {
  chipLabel: string;
  chipId: string;
  keys: string[];
  handleSubmit: (jsonData: Array<{ [key: string]: string }>) => void;
  taskCategory: string;
  configIds: ConfigIds[];
  configStatus: any;
  selectedStage: string;
  selectedCategory: string;
}

const CONFIG_TYPES = [
  { value: "DYNAMIC", label: "Task upload Data" },
  { value: "STATIC", label: "Static" },
  { value: "PREVIOUS_STAGE", label: "Previous stage" },
];

const taskUploadDataString = `Admin can refer to the uploaded data column by using @. Multiple config_name entries can be used in a single text area. Works the same as Previous Stage but uses the upload data source.`
const staticString = `Admin can directly enter the text to show to the Tasker.`
const previousStageString = `Admin can refer to a previous stage’s uploaded data column by using @stage_name.config_identifier. Multiple stage_name and config_name can be used in a single text area.`

const DESCRIPTIONS: Record<string, string> = {
  DYNAMIC: taskUploadDataString,
  PREVIOUS_STAGE: previousStageString,
  STATIC: staticString,
};

const MULTI_TURN_TYPE_OPTIONS = ['date', 'time', 'textfield'];

const JsonTypeContent: React.FC<JsonTypeContentProps> = ({ chipLabel, chipId, keys, handleSubmit, selectedCategory, taskCategory, configIds, configStatus, selectedStage }) => {
  const initialField = keys.reduce((acc, key) => ({ ...acc, [key]: '' }), {});
  const [jsonFields, setJsonFields] = useState<Array<{ [key: string]: string }>>([initialField]);
  const [errors, setErrors] = useState<{ [index: number]: { [key: string]: string } }>({});
  const [colorPickerOpenIndex, setColorPickerOpenIndex] = useState<number | null>(null);
  const [colorPickerValue] = useState<string>('#000000');
  const [formValues, setFormValues] = useState({ identifier: '', long_description: '' });
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const createStatus = useSelector((state: RootState) => state.config.createStatus);
  const updateStatus = useSelector((state: RootState) => state.config.updateStatus);
  const jsonData = useSelector((state: RootState) => state.config.currentConfig)
  const navigate = useNavigate()
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [selectedOption, setSelectedOption] = useState<'TASK' | 'REVIEW'>('TASK');
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const getConfig = useSelector((state: RootState) => state.config.currentConfig);
  const config_id = getConfig.id;
  const [configType, setConfigType] = useState<string>('DYNAMIC');
  const [options, setOptions] = useState<any[]>([]);
  const [defaultValueType, setDefaultValueType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [startConfig, setStartConfig] = useState<boolean>(false);
  const [endConfig, setEndConfig] = useState<boolean>(false);
  const [order, setOrder] = useState('');
  const [localIsChecked, setLocalIsChecked] = useState<boolean>(false);
  const categoryList = useSelector((state: RootState) => state.config.configCategories)
  const getConfigCategpryListStatus = useSelector((state: RootState) => state.config.getConfigCategoryListStatus)
  const createConfigMessage = useSelector((state: RootState) => state.config.createConfigMessage)
  const updateConfigMessage = useSelector((state: RootState) => state.config.updateConfigMessage)
  const [newFields, setNewFields] = useState({ label: '', description: '' });
  const [noLimit, setNoLimit] = useState(true);
  const [noOfTurns, setNoOfTurns] = useState('');
  const getStageWiseConfigData = useSelector((state: RootState) => state.config.getStageWiseConfigData)
  const [nextConfigId, setNextConfigId] = useState<ConfigIds>();
  let { configRowId } = useParams()
  const [configSticky, setConfigSticky] = useState<boolean>(false);
  const [autoOpen, setAutoOpen] = useState<boolean>(false);
  const [enableScoring, setEnableScoring] = useState<boolean>(false);
  const [scoringMode, setScoringMode] = useState<string>('AUTO');
  const [scoringTitle, setScoringTitle] = useState<string>('');

  useEffect(() => {
    if (configRowId) {
      dispatch(getConfigForTable({ project_id: id, id: configRowId }));
    }
  }, [configRowId, id]);

  useEffect(() => {
    dispatch(getReview({ project_id: id || '' }))
  }, [dispatch, id]);

  useEffect(() => {
    if (chipLabel === 'label' || chipLabel === 'audio_annotation' || chipLabel === 'image_annotation') {
      dispatch(getConfigCategoryList({ project_id: id }));
    }
  }, [chipId, id])

  const handleClearSelectionNextConfig = () => {
    setNextConfigId(undefined);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setLocalIsChecked(checked);
  };

  useEffect(() => {
    if (getConfig && getConfig.value) {
      const populatedFields = getConfig && getConfig.value && getConfig.value?.value?.map((item: any) =>
        keys.reduce((acc: any, key: string) => ({ ...acc, [key]: item[key] || '' }), {})
      );
      setJsonFields(populatedFields);
      setNewFields({ label: getConfig.value?.label, description: getConfig.value?.description });
      setNoOfTurns(getConfig.value?.no_of_turns ? String(getConfig.value.no_of_turns) : '');
      setNoLimit(!getConfig.value?.no_of_turns);
      setSelectedOption(getConfig.type);
      setOrder(getConfig.order);
      setLocalIsChecked(getConfig.required)
      setFormValues({ identifier: getConfig.identifier, long_description: getConfig.long_description });
      setLocalIsChecked(getConfig.required)
      setConfigType(getConfig.default_value_type || 'STATIC');
      setConfigSticky(getConfig.is_sticky)
      setStartConfig(getConfig.is_starting_config)
      setEndConfig(getConfig.is_end_config)
      setNextConfigId(configIds.find(item => item.id === getConfig.next_config_id))
      setSelectedStageId(getConfig.stage_id);
      setDefaultValueType(getConfig.default_value)
      setAutoOpen(getConfig.auto_open);
      setEnableScoring(getConfig.enable_scoring || false);
      setScoringMode(getConfig.scoring_mode || 'AUTO');
      setScoringTitle(getConfig.scoring_title || '');
    }
  }, [getConfig, keys, configIds]);

  const handleSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        handleColorPickerClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleStartConfigCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setStartConfig(checked);
  };

  const handleAutoOpenCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setAutoOpen(checked);
  };

  const handleFieldChange = (index: number, key: string, value: string) => {
    const newFields = jsonFields.map((field, i) =>
      i === index ? { ...field, [key]: value } : field
    );
    setJsonFields(newFields);
    if (errors[index] && errors[index][key]) {
      const newErrors = { ...errors };
      delete newErrors[index][key];
      setErrors(newErrors);
    }
  };

  const handleColorPickerChange = (color: any) => {
    if (colorPickerOpenIndex !== null) {
      const index = colorPickerOpenIndex;
      const newFields = [...jsonFields];
      newFields[index]['color'] = color.hex;
      setJsonFields(newFields);
      // setColorPickerValue(color.hex);
    }
  };

  const handleColorPickerToggle = (index: number) => {
    if (colorPickerOpenIndex === index) {
      setColorPickerOpenIndex(null);
    } else {
      setColorPickerOpenIndex(index);
    }
  };

  const handleColorPickerClose = () => {
    setColorPickerOpenIndex(null);
  };
  const handleAdd = () => {
    const newFields = [...jsonFields, initialField];
    setJsonFields(newFields);
  };

  const handleStickyCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setConfigSticky(checked);
  };

  const handleDelete = (index: number) => {
    const newFields = jsonFields?.filter((_, i) => i !== index);
    setJsonFields(newFields);
    const newErrors = { ...errors };
    delete newErrors[index];
    setErrors(newErrors);
  };

  const validateFields = () => {
    const newErrors: { [index: number]: { [key: string]: string } } = {};

    jsonFields?.forEach((fieldSet, index) => {
      keys.forEach(key => {
        if (key === 'range_labels') return;

        const value = fieldSet[key];
        if (typeof value === 'string' && !value.trim()) {
          if (!newErrors[index]) newErrors[index] = {};
          newErrors[index][key] = 'This field is required';
        } else if (value === undefined || value === null) {
          if (!newErrors[index]) newErrors[index] = {};
          newErrors[index][key] = 'This field is required';
        }
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = () => {
    console.log({ formValues })
    const missingFields = [];
    if (!formValues.identifier) missingFields.push('Unique Identifier');
    if (!newFields.label) missingFields.push('Title');
    if (!newFields.description) missingFields.push('Description');
    if (chipLabel === 'multi_turn' && !noLimit && !noOfTurns) missingFields.push('Number of turns');
    if (!defaultValueType && (chipLabel === 'label' || chipLabel === 'audio_annotation' || chipLabel === 'image_annotation')) missingFields.push('Config Data Type');
    if (enableScoring && scoringMode === 'MANUAL' && !scoringTitle.trim()) missingFields.push('Scoring Title');

    if (missingFields.length > 0) {
      handleSnackbar(`Please fill required fields: ${missingFields.join(", ")}`, "error");
      return;
    }
    if (validateFields()) {
      const payload = {
        ...(config_id && { id: config_id }),
        project_id: id,
        task_category_id: taskCategory,
        config_id: chipId,
        name: chipLabel,
        value: {
          label: newFields.label,
          description: newFields.description,
          ...(chipLabel === 'multi_turn' && { no_of_turns: noLimit ? null : Number(noOfTurns) }),
          value: jsonFields
        },
        long_description: formValues.long_description,
        ...(selectedCategory != "" && { type: selectedCategory }),
        next_config_id: nextConfigId === undefined ? '' : nextConfigId?.id,
        identifier: formValues.identifier,
        ...(order && Number(order) !== 0 && { order: Number(order) }),
        required: localIsChecked,
        is_sticky: configSticky,
        is_starting_config: startConfig,
        auto_open: autoOpen,
        is_end_config: endConfig,
        ...((configType && (chipLabel === 'label' || chipLabel === 'audio_annotation' || chipLabel === 'image_annotation')) && { default_value_type: configType }),
        ...((defaultValueType && (chipLabel === 'label' || chipLabel === 'audio_annotation' || chipLabel === 'image_annotation')) && { default_value: defaultValueType }),
        stage_id: typeof selectedStage === "string" && !isNaN(Number(selectedStage))
          ? Number(selectedStage)
          : selectedStage,
        enable_scoring: enableScoring,
        ...(enableScoring && { scoring_mode: scoringMode }),
        scoring_title: (enableScoring && scoringMode === 'MANUAL' && scoringTitle.trim()) ? scoringTitle.trim() : null,

      }

      const newUpdateValues = Object.keys(payload).reduce((acc, key) => {
        const fetchedValue = jsonData[key];
        const newValue = payload[key];
        if (
          key in jsonData &&
          newValue !== fetchedValue &&
          (fetchedValue !== '')
        ) {
          acc[key] = newValue;
        }

        return acc;
      }, {} as Record<string, string | boolean | string[] | undefined>);

      if (config_id) {
        dispatch(updateConfig({ id: config_id, ...newUpdateValues }))
      }
      else {
        dispatch(createConfig(payload));
      }
    }
  };

  useEffect(() => {
    if (createStatus === STATUS.SUCESS) {
      // handleSnackbar('Config Added Successfully', 'success');
      handleSnackbar(createConfigMessage, 'success');
      setTimeout(() => {
        navigate(`/project/${id}`);
      }, 1000);
    }

    if (createStatus === STATUS.FAILED) {
      // handleSnackbar('error occured please try again', 'warning');
      handleSnackbar(createConfigMessage, 'warning');
    }

    return () => {
      if (createStatus === STATUS.SUCESS || createStatus === STATUS.FAILED) {
        dispatch(setCreateConfigStatus(STATUS.NOT_STARTED));
      }
    };
  }, [createStatus]);

  useEffect(() => {
    const handleUpdateStatus = () => {
      if (updateStatus === STATUS.SUCESS) {
        // handleSnackbar('Config Updated Successfully', 'success');
        handleSnackbar(updateConfigMessage, 'success');
        setTimeout(() => {
          navigate(`/project/${id}`);
        }, 1000);
      }

      if (updateStatus === STATUS.FAILED) {
        // handleSnackbar('Error Occurred, Please try again!', 'error');
        handleSnackbar(updateConfigMessage, 'error');
      }
    };

    handleUpdateStatus();

    return () => {
      if (updateStatus == STATUS.SUCESS || updateStatus === STATUS.FAILED) {
        dispatch(setUpdateConfigStatus(STATUS.NOT_STARTED));
      }
    };
  }, [updateStatus, navigate, dispatch]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value.trim());
  };

  const debouncedResults = useMemo(() => { return debounce(handleSearchChange, 300); }, []);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  useEffect(() => {
    if (getConfigCategpryListStatus === STATUS.SUCESS) {
      setOptions(categoryList)
    }
  }, [getConfigCategpryListStatus])

  const handleLongDesc = (newContentHTML: { json: string; html: string }) => {
    console.log({ newContentHTML })

    setFormValues((prevState: any) => ({
      ...prevState,
      long_description: newContentHTML.html,
    }));
  };
  
  const handleConfigDataType = (html: string) => {
    setDefaultValueType(html);
  };

  const handleDropDownChange = (index: number, value: string) => {
    // handleTagChange(index, value);
    setNextConfigId(configIds.find(item => item.identifier === value));
  }

  console.log({ formValues });

  return (
    <>
      {/* <div style={{ display: 'flex' }}>
        <Typography sx={{ fontFamily: 'Montserrat', fontSize: 20, fontWeight: 600, lineHeight: '24px', color: '#10383D', marginTop: 2, marginBottom: 2, textTransform: 'capitalize', }}>
          {chipLabel.replace(/_/g, ' ')}
        </Typography>
      </div> */}
      <div style={{ display: 'flex', marginBottom: '20px', marginTop: '20px' }}>
        <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D', marginTop: '5px' }}>
          Enter Unique Identifier:<br />
          <Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 400, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D' }}> This Name will identify unique configs </Typography>
        </Typography>
        <TextField
          placeholder="Enter Identifier"
          name="formValues"
          value={formValues.identifier}
          onChange={(e) => {
            const value = e.target.value;
            const sanitizedValue = value.replace(/[^a-zA-Z0-9_]/g, '');
            setFormValues({ identifier: sanitizedValue, long_description: formValues.long_description });
          }}
          sx={{ marginLeft: '70px', marginTop: '10px' }}
          size="small"
        />
      </div>

      <div style={{ display: 'flex', marginBottom: '20px', marginTop: '20px' }}>
        <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D', marginTop: '5px' }}>
          Enter Value:<br />
          <Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 400, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D' }}> This Name will identify unique configs </Typography>
        </Typography>
        <TextField
          placeholder="Title"
          name="label"
          value={newFields.label}
          onChange={(e) => {
            setNewFields({ ...newFields, label: e.target.value });
          }}
          sx={{ marginLeft: '70px', marginTop: '10px' }}
          size="small"
        />

        <TextField
          placeholder="Description"
          name="description"
          value={newFields.description}
          onChange={(e) => {
            setNewFields({ ...newFields, description: e.target.value });
          }}
          sx={{ marginLeft: '10px', marginTop: '10px' }}
          size="small"
        />
      </div>

      <Box
        display="flex"
        flexDirection="row"
        alignItems="flex-start"
        gap={6}
        mb={2}
      >
        <Box sx={{ minWidth: 200 }}>
          <Typography
            sx={{
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              lineHeight: '24px',
              color: '#10383D',
              mb: 0.5
            }}
          >
            Enter Labels:
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Montserrat',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '20px',
              color: '#10383D'
            }}
          >
            These labels will be visible on expert side
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" flex={1} gap={1.5}>
          {jsonFields && jsonFields.map((fieldSet, index) => (
            <Box
              key={index}
              display="flex"
              alignItems="flex-start"
              gap={1.5}
              sx={{ width: '100%', flexWrap: 'nowrap' }}
            >
                {keys.map((key) => (
                  <Box key={key} sx={{ position: 'relative', flex: key === 'type' && chipLabel === 'multi_turn' ? '0 0 270px' : '1 1 0', minWidth: 0 }}>
                    {chipLabel === 'multi_turn' && key === 'type' ? (
                      <>
                        <FormControl size="small" error={errors[index] && !!errors[index][key]} fullWidth>
                          <Select
                            value={fieldSet[key] || ''}
                            displayEmpty
                            onChange={(e) => handleFieldChange(index, key, e.target.value)}
                          >
                            <MenuItem value="" disabled>
                              type
                            </MenuItem>
                            {MULTI_TURN_TYPE_OPTIONS.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {errors[index] && errors[index][key] && (
                          <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', lineHeight: 1.66, marginLeft: '14px', marginTop: '3px' }}>
                            {errors[index][key]}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <TextField
                        size="small"
                        value={fieldSet[key]}
                        placeholder={key}
                        variant="outlined"
                        fullWidth
                        type="text"
                        onChange={(e) => handleFieldChange(index, key, e.target.value)}
                        error={errors[index] && !!errors[index][key]}
                        helperText={errors[index] && errors[index][key]}
                        InputProps={{
                          startAdornment: key === 'color' ? (
                            <InputAdornment position="start">
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  backgroundColor: fieldSet['color'],
                                  borderRadius: '10%',
                                  border: '1px solid #ccc',
                                }}
                              />
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                    )}
                    {key === 'color' && (
                      <>
                        <IconButton
                          onClick={() => handleColorPickerToggle(index)}
                          aria-label="toggle color picker"
                          style={{ position: 'absolute', right: '5px', top: '10%' }}
                        >
                          <ColorizeIcon />
                        </IconButton>
                        {colorPickerOpenIndex === index && (
                          <div
                            ref={colorPickerRef}
                            style={{ position: 'absolute', zIndex: 999, top: '100%', left: 0 }}
                          >
                            <ChromePicker
                              color={colorPickerValue}
                              onChange={handleColorPickerChange}
                              disableAlpha
                            />
                          </div>
                        )}
                      </>
                    )}
                  </Box>
                ))}
                <Box sx={{ width: 44, flexShrink: 0, display: 'flex', justifyContent: 'center', pt: '4px' }}>
                  {jsonFields.length > 1 && (
                  <IconButton
                    onClick={() => handleDelete(index)}
                    aria-label="delete row"
                    sx={{ color: '#ec480f' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                  )}
                </Box>
            </Box>
          ))}
          <Box>
            <IconButton
              style={{
                backgroundColor: '#3D85C6',
                color: '#fff',
                padding: '7px',
                borderRadius: '50%',
                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.3s ease',
              }}
              onClick={handleAdd}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <AddIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

       {chipLabel === 'multi_turn' && (
        <div style={{ display: 'flex', marginBottom: '20px', marginTop: '20px' }}>
          <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D', marginTop: '5px' }}>
            No limit<br />
            <Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 400, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D' }}> Keep conversation turns unlimited </Typography>
          </Typography>
          <Checkbox
            checked={noLimit}
            onChange={(e) => {
              setNoLimit(e.target.checked);
              if (e.target.checked) setNoOfTurns('');
            }}
            color="primary"
            sx={{ marginLeft: '80px', marginTop: '5px' }}
          />
          {!noLimit && (
            <TextField
              placeholder="Number of turns"
              name="no_of_turns"
              value={noOfTurns}
              onChange={(e) => {
                const value = e.target.value;
                setNoOfTurns(value.replace(/[^0-9]/g, ''));
              }}
              sx={{ marginLeft: '24px', marginTop: '10px' }}
              size="small"
              type="number"
              inputProps={{ min: 1 }}
            />
          )}
        </div>
      )}

      <div style={{ display: 'flex', marginBottom: '20px', gap: 80 }}>

        <div>
          <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D', marginTop: '5px' }}>
            Enter Long Description:<br />
          </Typography>
          <Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 400, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D' }}> This would be visible on Expert Side. </Typography>
        </div>

        <div>
          <RichTextEditor
            key={getConfig?.id || 'editor_placeholder_key'}
            onChange={handleLongDesc}
            initialContent={getConfig?.long_description || ''}
          />
        </div>
      </div>

      {(chipLabel === 'label' || chipLabel === 'audio_annotation' || chipLabel === 'image_annotation') &&
        (<>
          <FormControl size="small" >
            <div style={{ display: 'flex', gap: 174 }}>
              <div>
                <Typography
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "24px",
                    letterSpacing: "0px",
                    textAlign: "left",
                    color: "#10383D",
                    marginTop: "5px",
                  }}
                >
                  Config Data Type:
                </Typography>
              </div>
              <div style={{ display: "absolute", justifyContent: "space-around", alignItems: "center", gap: 10 }}>
                <div>
                  <Select
                    labelId="config-type-label"
                    id="config-type"
                    // label="Config Type"
                    value={configType}
                    onChange={(e: any) => setConfigType(e.target.value)}
                    sx={{ minWidth: 260, maxWidth: 350, alignItems: "flex-start", whiteSpace: "normal" }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          width: 320,
                          maxWidth: 320,
                          '& .MuiMenuItem-root': {
                            alignItems: 'flex-start',
                            whiteSpace: 'normal',
                          },
                        },
                      },
                    }}

                  >
                    {CONFIG_TYPES.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <ListItemText
                          primary={opt.label}
                          secondary={DESCRIPTIONS[opt.value]}
                          primaryTypographyProps={{ fontSize: 17, fontWeight: 400, lineHeight: 1.2, fontFamily: "montserrat" }}
                          secondaryTypographyProps={{
                            sx: {
                              mt: 0.5,
                              fontSize: 12,
                              color: 'text.secondary',
                              whiteSpace: 'normal',
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-word',
                              hyphens: 'auto',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            },
                          }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                <div>
                  {configType && <CustomRichTextEditor type={configType} column_names={options} previous_stages={getStageWiseConfigData.data?.results} initialContent={getConfig?.default_value || ""}  onContentChange={handleConfigDataType}/>}
                  
                </div>
              </div>
            </div>
          </FormControl>
        </>)
      }

      <div style={{ display: 'flex', gap: 188 }}>
        <div>
          <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D', marginTop: '15px' }}>
            Select Next Config
          </Typography>
        </div>
        {configStatus === "loading" && <Typography>Loading...</Typography>}
        <div>
          {configStatus === "success" && (
            <FormControl sx={{ m: 1, minWidth: 100, marginBottom: "15px", display: "flex", flexDirection: "row", alignItems: "center" }}>

              <Select
                variant="standard"
                labelId={`demo-simple-select-label`}
                id={`demo-simple-select}`}
                label="Tag"
                value={nextConfigId !== undefined ? nextConfigId.identifier : ''}
                onChange={(e) => handleDropDownChange(0, e.target.value)}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Next Config
                </MenuItem>
                {/* Dynamically generate MenuItem for each identifier */}
                {configIds.map((dataItem) => (
                  <MenuItem key={dataItem.id} value={dataItem.identifier}>
                    {dataItem.identifier ? dataItem.identifier : "No Name"}
                  </MenuItem>
                ))}
              </Select>
              {nextConfigId && (
                <Button
                  onClick={handleClearSelectionNextConfig}
                  size="small"
                  sx={{ minWidth: "auto" }}
                >
                  Clear
                </Button>
              )}
            </FormControl>
          )}
        </div>
        {configStatus === "error" && (
          <Typography sx={{ color: "red" }}>Error fetching configs. Please try again.</Typography>
        )}
      </div>
      <div style={{ display: 'flex', gap: 53 }}>
        <div>
          <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D', marginTop: '15px' }}>
            Do you want to stick this config?
          </Typography>
        </div>
        <div>
          <Checkbox
            checked={configSticky}
            onChange={handleStickyCheckboxChange}
            color="primary"
            sx={{ marginTop: '5px', marginLeft: '30px' }}
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 180 }}>
        <div>
          <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D', marginTop: '15px' }}>
            Is this start config?
          </Typography>
        </div>
        <div>
          <Checkbox
            checked={startConfig}
            onChange={handleStartConfigCheckboxChange}
            color="primary"
            sx={{ marginTop: '5px' }}
          />
        </div>
      </div>

      <div style={{ display: "flex", gap: "231px" }}>
        <Typography
          sx={{
            fontFamily: "Montserrat",
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "24px",
            letterSpacing: "0px",
            textAlign: "left",
            color: "#10383D",
            marginTop: "15px",
          }}
        >
          Auto Open?
        </Typography>
        <Checkbox
          checked={autoOpen}
          onChange={handleAutoOpenCheckboxChange}
          color="primary"
          sx={{ marginTop: "5px" }}
        />
      </div>

      <ScoringConfigSection
        enableScoring={enableScoring}
        scoringMode={scoringMode}
        scoringTitle={scoringTitle}
        onEnableScoringChange={setEnableScoring}
        onScoringModeChange={setScoringMode}
        onScoringTitleChange={setScoringTitle}
        gap={213}
      />
      <div style={{ display: 'flex', gap: 78 }}>
        <div>
          <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D', marginTop: '15px' }}>
            Do you want to mark it required?
          </Typography>
        </div>
        <div>
          <Checkbox
            checked={localIsChecked}
            onChange={handleCheckboxChange}
            color="primary"
            sx={{ marginTop: '5px' }}
          />
        </div>
      </div>
      <br />
      <AddButton
        disabled={config_id ? updateStatus == STATUS.PENDING : createStatus == STATUS.PENDING}
        startIcon={config_id ? (updateStatus == STATUS.PENDING ? <CircularProgress size={20} /> : <></>) : (createStatus == STATUS.PENDING ? <CircularProgress size={20} /> : null)}
        onClick={handleFormSubmit} variant="contained"
      >
        {config_id ? "Update" : "Submit"}
      </AddButton>
      <SnackbarNotification
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </>

  );
};

export default JsonTypeContent;

